import { useMemo } from 'react'
import { Breadcrumb, Modal, ProgressIndicator } from '@/components/atoms'
import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import { ActivitySetUp } from '@/containers'
import { isNotEmptyAndDefined } from '@/helpers/shadcn/utils'
import { useAppSelector } from '@/store'
import { resetActivityData, setActivityFormData } from '@/store/slices/activitySlice'
import { ACTIVITY_TYPE } from '@/types/Activity'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'
import { ROUTES } from '@/utils/constants/routesConstants'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook'
import { COUNTRY_CODES_FREQUENCY } from '@/utils/constants/globalConstants'

const AddActivity = () => {
  const [isCancel, setIsCancel] = useState(false)
  const { formData } = useSelector(state => state.activity)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const localCurrencyCode = useAppSelector(state => state.common.localCurrency)
  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const isCountryCodeFrequency = useMemo(() => COUNTRY_CODES_FREQUENCY.includes(countryCode), [countryCode]);
  const { handleDraft: handleReviewDraft } = useActivityReview()
  const {
    frequency,
    pointsEarned,
    pointsEarnedValid,
    activityType,
    hasEndDate,
    startDate,
    endDate,
    dailyLoginLimit,
    dailyLoginLimitValid,
    allTimeLoginLimit,
    allTimeLoginLimitValid,
    startDateValid,
    endDateValid,
    activityName,
    orderType,
    localCurrency,
    localCurrencyValid,
    numberOfCases,
    numberOfCasesValid,
    receivePoints
  } = formData

  // Order alignment for Welcome review page
  const welcomeActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    hasEndDate: hasEndDate,
    startDate: startDate,
    ...(hasEndDate && { endDate: endDate }),
  }

  // Order alignment for Login review page
  const loginActivityOrder = {
    activityType: activityType,
    ...(frequency && { frequency: frequency }),
    pointsEarned: pointsEarned,
    ...(dailyLoginLimit && { dailyLoginLimit: dailyLoginLimit }),
    ...(allTimeLoginLimit && { allTimeLoginLimit: allTimeLoginLimit }),
    startDate: startDate,
    endDate: endDate,
    receivePoints: receivePoints
  }

  const pointsOnOrderDeliveryActivityOrder = () => {
    const fieldsArranged = {
      activityType: activityType,
      activityName: activityName,
      orderType: orderType,
    }
    if (isCountryCodeFrequency) {
      fieldsArranged.numberOfCases = numberOfCases,
        fieldsArranged.pointsEarned = pointsEarned
    }
    else {
      fieldsArranged.localCurrency = localCurrency,
        fieldsArranged.pointsEarned = pointsEarned
    }
    fieldsArranged.startDate = startDate,
      fieldsArranged.endDate = endDate
    return fieldsArranged
  }

  const handleDraft = (event) => {
    event.stopPropagation();
    handleReviewDraft();
  }

  const handleNext = () => {
    const formDataWithOrder = getFormDataWithOrder()
    dispatch(setActivityFormData(formDataWithOrder))
    navigate(ROUTES.ACTIVITY_REVIEW)
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  const isInvalidLoginData = () => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate)
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    let isReceivePointsInvalid = false;
    isReceivePointsInvalid = !isNotEmptyAndDefined(receivePoints)

    let isFrequencyInvalid = false;
    if (COUNTRY_CODES_FREQUENCY.includes(countryCode)) {
      isFrequencyInvalid = !isNotEmptyAndDefined(frequency)
    }
    return pointsEarnedValid || dailyLoginLimitValid || allTimeLoginLimitValid || isStartDateInvalid || isEndDateInvalid || isReceivePointsInvalid || isPointsEarnedInvalid || startDateValid || endDateValid || isFrequencyInvalid
  }

  const isInvalidWelcomeData = () => {
    const isStartDateInvalid = validateDate(startDateValid)
    const isEndDateInvalid = validateDate(endDateValid)
    if (pointsEarnedValid === undefined) return true

    // Check if the form data has an end date
    if (hasEndDate) {
      return pointsEarnedValid || isStartDateInvalid || isEndDateInvalid
    }
    // Otherwise, only check start date and points
    return pointsEarnedValid || isStartDateInvalid
  }

  const isInvalidPointsOrderDeliver = () => {
    const isStartDateInvalid = validateDate(startDateValid)
    const isEndDateInvalid = validateDate(endDateValid)
    const isCurrencyFrequencyValid = isCountryCodeFrequency ? (numberOfCasesValid || !isNotEmptyAndDefined(numberOfCases)) : (localCurrencyValid || !isNotEmptyAndDefined(localCurrency))
    return (
      pointsEarnedValid ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      !isNotEmptyAndDefined(orderType)
    )
  }

  const validateDate = dateValid => {
    return dateValid === undefined || dateValid === true
  }

  const validateFormData = () => {
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return isInvalidLoginData()
    } else if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return isInvalidWelcomeData()
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return isInvalidPointsOrderDeliver()
    } else if (handleHideSaveDraft()) {
      return true
    }
  }

  // Remove keys if they are empty
  const getFormDataWithOrder = () => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return welcomeActivityOrder
    } else if (activityType === ACTIVITY_TYPE.LOGIN) {
      return loginActivityOrder
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return pointsOnOrderDeliveryActivityOrder()
    }
  }

  const onClickPrimaryButton = () => {
    handleResetFormData()
    navigate(ROUTES.SALES_TARGETS + '?tab=0')
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false,
    }
    dispatch(resetActivityData(defaultFormData))
  }

  const handleHideSaveDraft = () => {
    return formData.activityType === '' || formData.activityType === undefined
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },

            {
              href: '',
              label: 'Add Activity Mexico',
            },
          ],
        }}
        id={'aa_bradcrumb'}
      />

      <PageHeader title={'Add Activity'} />

      <ProgressIndicator currentStep={1} completed={false} steps={2} titles={[{ title: 'Setup' }, { title: 'Review' }]} />
      <br />
      <ActivitySetUp localCurrencyCode={localCurrencyCode} />

      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title="Are you sure you want to cancel?"
          text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      ) : null}

      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={validateFormData()}
        hideSaveDraft={handleHideSaveDraft()}
      />
    </>
  )
}
export default AddActivity
